/**
 * "What did the calling cost?", as a screen.
 *
 * At the end of a month nobody could say whether four hundred calls had cost
 * twelve euros or a hundred and twenty. `CallLog` carried no monetary column at
 * all, and the four token columns that had been filling up on every call since
 * the co-pilot shipped were read by nothing anywhere.
 *
 * THREE NUMBERS, KEPT APART. Twilio's charge for the legs, the model's tokens,
 * and - deliberately absent - recording storage, which is a monthly rent per
 * file rather than a cost of these calls and is counted in the settings screen
 * that can actually do something about it. Rolling them into one figure would
 * make the total unreadable in the exact case somebody opens this screen for:
 * wanting to know which of them went up.
 *
 * Read-only. Nothing here edits anything.
 */
import { useCallback, useEffect, useState } from "react";
import { Coins, Loader2, TriangleAlert } from "lucide-react";
import { cn } from "@wa/ui";
import { useT } from "../../../../i18n/I18nProvider";
import { getCallCost, type CostReport, type CostSlice } from "../../../../lib/callCost";
import { EmptyState, fmtNum } from "../../../ui";
import { RailTrigger } from "../../_shared/workMobile";
import {
  COST_DAYS,
  costPeriod,
  fmtDuration,
  fmtMicros,
  fmtMoney,
  fmtTokens,
  type CostDays,
} from "./money";

/** The dash a figure nobody has is drawn as. Never a zero - see `money.ts`. */
const NONE = "-";

/** One summary figure at the top of the screen. */
function Stat({ label, value, note }: { label: string; value: string; note?: string | null }) {
  return (
    <div className="crm-cost-stat">
      <span className="crm-cost-stat-l">{label}</span>
      <strong className="crm-cost-stat-v">{value}</strong>
      {note ? <span className="crm-cost-stat-n">{note}</span> : null}
    </div>
  );
}

/** The AI cell, which has three different things to say. */
function aiCell(slice: CostSlice, t: ReturnType<typeof useT>): string {
  if (slice.ai.tokensIn === 0 && slice.ai.tokensOut === 0) return NONE;
  const money = fmtMicros(slice.ai.micros);
  // Tokens were spent but the model has no rate: show what was spent and refuse
  // to put a number on it. A figure quietly produced from a neighbouring rate
  // would be worse than the blank it replaced.
  if (money === null) return t("calls.cost.unpricedModel");
  return `${money} USD`;
}

export function CostPanel({ onRail }: { onRail: () => void }) {
  const t = useT();
  const [days, setDays] = useState<CostDays>(30);
  const [report, setReport] = useState<CostReport | null>(null);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setReport(null);
    try {
      // `new Date()` at fetch time rather than in state, so a desk left open
      // overnight does not keep asking about yesterday.
      const { from, to } = costPeriod(days, new Date());
      setReport(await getCallCost(from, to));
      setError(null);
    } catch {
      setError(t("calls.cost.loadError"));
    }
  }, [days, t]);

  useEffect(() => {
    void load();
  }, [load]);

  return (
    <div className="crm-card crm-work-col">
      <div className="crm-pane-head pt-3.5">
        <RailTrigger label={t("calls.pane.views")} onOpen={onRail} />
        <span className="crm-cov-head">
          <Coins className="size-4" />
          {t("calls.cost.title")}
        </span>
        {report === null && !error ? (
          <Loader2 className="size-4 animate-spin text-muted-foreground" />
        ) : null}
        <div className="crm-seg crm-cov-seg ml-auto">
          {COST_DAYS.map((option) => (
            <button
              key={option}
              type="button"
              className={cn("crm-seg-b", days === option && "is-on")}
              onClick={() => setDays(option)}
              aria-pressed={days === option}
            >
              {t("calls.cost.days", { n: option })}
            </button>
          ))}
        </div>
      </div>

      <div className="crm-cov-body">
        {error ? (
          <div className="mb-2 flex items-center gap-2 text-[12.5px] text-destructive">
            <TriangleAlert className="size-3.5 shrink-0" />
            {error}
          </div>
        ) : null}

        {report?.truncated ? (
          <div className="mb-2 flex items-center gap-2 text-[12.5px] text-destructive">
            <TriangleAlert className="size-3.5 shrink-0" />
            <span>{t("calls.cost.truncated")}</span>
          </div>
        ) : null}

        {report && report.calls === 0 ? (
          <EmptyState
            icon={Coins}
            title={t("calls.cost.emptyTitle")}
            body={t("calls.cost.emptyNote")}
          />
        ) : null}

        {report && report.calls > 0 ? (
          <>
            <div className="crm-cost-stats">
              <Stat label={t("calls.cost.calls")} value={fmtNum(report.calls)} />
              <Stat label={t("calls.cost.talkTime")} value={fmtDuration(report.seconds)} />
              <Stat
                label={t("calls.cost.telephony")}
                value={fmtMoney(report.money) ?? NONE}
                // Said out loud rather than left to be inferred from a total
                // that looks complete. A price lands minutes after a call, so on
                // any recent period some of them are genuinely still missing,
                // and "so far" is a different claim from "this month cost".
                note={
                  report.unpricedCalls > 0
                    ? t("calls.cost.awaitingPrice", { n: report.unpricedCalls })
                    : null
                }
              />
              <Stat
                label={t("calls.cost.ai")}
                value={aiCell(report, t)}
                note={
                  report.ai.tokensIn > 0 || report.ai.tokensOut > 0
                    ? t("calls.cost.tokens", {
                        in: fmtTokens(report.ai.tokensIn),
                        out: fmtTokens(report.ai.tokensOut),
                      })
                    : null
                }
              />
            </div>

            {/* The estimate's terms, in one line and always visible. It is list
                pricing for the configured model, so on a free-tier key it is
                what the same usage WOULD have cost - which is exactly the
                number somebody wants before moving off it, and exactly the
                number that would mislead if it were labelled as a bill. */}
            <p className="crm-cost-terms">{t("calls.cost.aiNote", { model: report.model })}</p>

            {/* Scrolls inside its own box: one row per operator is fine, but a
                narrow reading pane plus four columns is how a page ends up
                scrolling sideways as a whole and taking the rail with it. */}
            <div className="crm-cov-scroll">
              <table className="crm-cov-tbl crm-cost-tbl">
                <thead>
                  <tr>
                    <th scope="col">{t("calls.cost.operator")}</th>
                    <th scope="col">{t("calls.cost.calls")}</th>
                    <th scope="col">{t("calls.cost.talkTime")}</th>
                    <th scope="col">{t("calls.cost.telephony")}</th>
                    <th scope="col">{t("calls.cost.ai")}</th>
                  </tr>
                </thead>
                <tbody>
                  {report.operators.map((op) => (
                    <tr key={op.userId || "unclaimed"}>
                      <th scope="row">{op.name ?? t("calls.cost.unclaimed")}</th>
                      <td>{fmtNum(op.calls)}</td>
                      <td>{fmtDuration(op.seconds)}</td>
                      <td>{fmtMoney(op.money) ?? NONE}</td>
                      <td>{aiCell(op, t)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        ) : null}
      </div>
    </div>
  );
}
